import React from 'react';
import { Check } from 'lucide-react';

interface StepProgressProps {
  currentStep: number;
  steps: string[];
  onStepClick?: (step: number) => void;
}

export default function StepProgress({ currentStep, steps, onStepClick }: StepProgressProps) {
  const progress = (currentStep / steps.length) * 100;

  const handleStepClick = (stepNumber: number) => {
    if (onStepClick && stepNumber !== currentStep) {
      onStepClick(stepNumber);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="space-y-4">
        {/* Title and Progress */}
        <div className="flex justify-between text-sm">
          <span className="font-semibold text-gray-800">{steps[currentStep - 1]}</span>
          <span className="text-gray-500">{Math.round(progress)}%</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-black h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Step Labels */}
        <div className="flex justify-between text-xs overflow-x-auto gap-1">
          {steps.map((step, index) => {
            const stepNumber = index + 1;
            const isCompleted = stepNumber < currentStep;
            const isCurrent = stepNumber === currentStep;
            const isClickable = onStepClick && stepNumber !== currentStep;

            return (
              <div
                key={index}
                onClick={() => handleStepClick(stepNumber)}
                className={`flex flex-col items-center text-center min-w-[70px] transition-all duration-200 ${
                  isCurrent
                    ? 'text-blue-600 font-semibold'
                    : isCompleted
                      ? 'text-gray-600'
                      : 'text-gray-400'
                } ${
                  isClickable
                    ? 'cursor-pointer hover:text-blue-600 hover:font-semibold'
                    : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-1">
                  {isCompleted && (
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                  )}
                  <span className="whitespace-nowrap text-[10px] md:text-xs leading-tight">
                    {step}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
